import { useEffect, useState, useCallback } from "react";

import { usePlaidLink } from "react-plaid-link";
import { createPlaidLinkToken, exchangePlaidPublicToken } from "../shared/_services/common.services";

const usePlaidIntegration = (initialRedirectUri) => {
    const [linkToken, setLinkToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExchangingToken, setIsExchangingToken] = useState(false);
    
    
    // User details fetch 
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    const userId = currentUser?.user?._id;
    const token = currentUser?.token;

    // Modal states
    const [showPlaidModal, setShowPlaidModal] = useState(false);
    const [modalVariant, setModalVariant] = useState('success');
    const [modalMessage, setModalMessage] = useState('');
    const [modalSecondaryMessage, setModalSecondaryMessage] = useState('');

    // Plaid Link Token Create 
    useEffect(() => {
        if (!token || !userId) {
            setIsLoading(false);
            return;
        }

        const redirectUri = `${process.env.REACT_APP_URL}${initialRedirectUri}`; 

        createPlaidLinkToken(userId, redirectUri)
            .then((token) => {
                setLinkToken(token);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error creating Plaid Link token:", error);
                setModalVariant('error');
                setModalMessage('We couldn’t connect your accounts.');
                setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
                setShowPlaidModal(true);
                setIsLoading(false);
            });
    }, [token, userId]); 

    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Plaid onSuccess callback function
    const handlePlaidSuccess = useCallback(async (public_token) => {
        const startTime = Date.now();
        setIsExchangingToken(true);
        try {
            // SHOW INFO MODAL FIRST
            setModalVariant('info');
            setModalMessage('Connecting your accounts…');
            setModalSecondaryMessage('This usually takes just a few seconds.\nYour information is encrypted and secure.');
            setShowPlaidModal(true);

            // API CALL
            await exchangePlaidPublicToken(public_token, userId);

            // ENSURE INFO STATE STAYS FOR MIN 2 SECONDS
            const elapsed = Date.now() - startTime;
            if (elapsed < 2000) {
                await wait(2000 - elapsed);
            }

            // SUCCESS STATE
            setModalVariant('success');
            setModalMessage('Your accounts are connected!');
            setModalSecondaryMessage('We pulled in the details we could. You can add or edit anything manually — estimates are totally fine.');

        } catch (error) {
            console.error("Error exchanging Plaid token:", error);

            // ENSURE INFO STATE STAYS FOR MIN 2 SECONDS EVEN ON ERROR
            const elapsed = Date.now() - startTime;
            if (elapsed < 2000) {
                await wait(2000 - elapsed);
            }

            setModalVariant('error');
            setModalMessage('We couldn’t connect your accounts.');
            setModalSecondaryMessage('This happens sometimes with bank security settings. You can try again, or enter things manually — whatever you prefer.');
            setShowPlaidModal(true);
        } finally {
            setIsExchangingToken(false);
        }
    }, [userId]); 

    // Plaid onExit callback
    const handlePlaidExit = useCallback((error) => {
        if (error) {
            setModalVariant('warning');
            setModalMessage('Your bank didn’t authorize the connection.');
            setModalSecondaryMessage('This is common if your bank requires extra verification. You can try again, or enter your info manually — your advisor can help.');
            setShowPlaidModal(true);
        }
    }, []); 

    // Plaid Link hook initialization
    const { open, ready, error } = usePlaidLink({
        token: linkToken,
        onSuccess: handlePlaidSuccess,
        onExit: handlePlaidExit,
    });
    
    return {
        openPlaid: open,
        isPlaidReady: ready && !isLoading, 
        plaidError: error,
        isTokenLoading: isLoading,
        isExchangingToken,
        // Modal states
        showPlaidModal,
        modalVariant,
        modalMessage,
        modalSecondaryMessage,
        setShowPlaidModal,
        onTryAgain: open,
    };
};

export default usePlaidIntegration;